// =============================================
// ENUMS
// =============================================

export enum EntityStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED'
}

export enum EntityType {
  PRODUCT = 'PRODUCT',
  SUBSCRIPTION = 'SUBSCRIPTION',
  API = 'API',
  CATEGORY = 'CATEGORY',
  DOCUMENT = 'DOCUMENT',
  FAQ = 'FAQ',
  LINK = 'LINK',
  MCP_SERVER = 'MCP_SERVER',
  MEDIA = 'MEDIA',
  SPECIFICATION = 'SPECIFICATION',
  SUPPORT_CASE = 'SUPPORT_CASE',
  AI_AGENT = 'AI_AGENT',
  APP_KYC = 'APP_KYC',
  GATEWAY_SKU = 'GATEWAY_SKU',
  GATEWAY_ROUTE = 'GATEWAY_ROUTE',
  DEVELOPER_GUIDE = 'DEVELOPER_GUIDE',
  OPERATIONAL_ERROR = 'OPERATIONAL_ERROR',
  API_INTAKE = 'API_INTAKE'
}

export enum OperationalErrorCategory {
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  VALIDATION = 'VALIDATION',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  RATE_LIMIT = 'RATE_LIMIT',
  SERVER = 'SERVER',
}

export enum RagIndexStatus {
  PENDING = 'PENDING',
  INDEXING = 'INDEXING',
  READY = 'READY',
  FAILED = 'FAILED',
  EXCLUDED = 'EXCLUDED',
  EXPIRED = 'EXPIRED',
}

export enum GuideSourceFormat {
  TEXT = 'TEXT',
  MARKDOWN = 'MARKDOWN',
  PDF_EXTRACT = 'PDF_EXTRACT',
}

export enum AppKeyStatus {
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED',
  DISABLED = 'DISABLED',
  INACTIVE = 'INACTIVE'
}

export enum GatewaySkuStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  DEPRECATED = 'DEPRECATED',
}

export enum GatewayType {
  APIGEE_EDGE = 'APIGEE_EDGE',
  APIGEE_OPDK = 'APIGEE_OPDK',
  APIGEE_HYBRID = 'APIGEE_HYBRID',
  APIGEE_X = 'APIGEE_X',
}

// =============================================
// BASE INTERFACE (Common fields for all entities)
// =============================================

export interface BaseEntity {
  /** Unique identifier (ID!) */
  uuid: string;
  
  /** Tenant/organization ID (String!) */
  tenantId: string;
  
  /** Entity type (EntityType!) */
  type: EntityType;
  
  /** Entity name (String!) */
  name: string;
  
  /** Creator user ID (String!) */
  createdBy: string;
  
  /** Creation timestamp ISO string (String!) */
  createdOn: string;
  
  /** Entity status (EntityStatus!) */
  status: EntityStatus;
  
  /** Array of tags ([String!]!) */
  tags: string[];
  
  /** Optional description (String) */
  description?: string | null;
  
  /** Optional classification labels (String) */
  classifications?: string | null;
  
  /** Optional entity classification (String) */
  entityClass?: string | null;
}

// =============================================
// CORE ENTITIES
// =============================================

export interface Product extends BaseEntity {
  /** Related APIs ([API!]!) */
  apis: API[];
  
  /** Product categories ([Category!]!) */
  categories: Category[];
  
  /** Related documents ([Document!]!) */
  documents: Document[];
  
  /** Product FAQs ([FAQ!]!) */
  faqs: FAQ[];
  
  /** Related links ([Link!]!) */
  links: Link[];

  /** Developer integration guides ([DeveloperGuide!]!) */
  developerGuides?: DeveloperGuide[];
  
  /** Related/similar products ([Product!]!) */
  relatedProducts: Product[];
  
  /** Main product image (Media) */
  heroImage?: Media | null;
  
  /** Whether subscription requests for this product need approval */
  approvalNeeded?: boolean;
  
  /** Emails of users who can approve subscription requests for this product */
  productOwners?: string[];

  /** True when product is ACTIVE and has at least one ACTIVE gateway SKU attachment */
  subscribable?: boolean;

  /** Gateway SKU links for subscription availability */
  gatewaySkuAttachments?: ProductGatewaySkuAttachment[];
}

/** Lightweight product row for the public catalog list */
export interface ProductCatalogItem {
  uuid: string;
  name: string;
  description?: string | null;
  status: EntityStatus;
  type: EntityType;
  tags: string[];
  categoryNames: string[];
  heroImageName?: string | null;
  subscribable?: boolean;
}

export interface GatewaySku extends BaseEntity {
  gatewayProductIdentifier: string;
  gatewayProductName: string;
  gatewayCommonName: string;
  gatewayId: string;
  gatewayType: GatewayType;
  envName: string;
  skuStatus: GatewaySkuStatus;
  syncedOn?: string | null;
  updatedOn?: string | null;
}

export interface ProductGatewaySkuAttachment {
  uuid: string;
  attachmentName?: string | null;
  gatewaySku: GatewaySku;
}

export enum RouteAuthMechanism {
  NONE = 'NONE',
  API_KEY = 'API_KEY',
  BASIC = 'BASIC',
  OAUTH2 = 'OAUTH2',
  JWT = 'JWT',
  MUTUAL_TLS = 'MUTUAL_TLS',
  CUSTOM = 'CUSTOM',
}

export enum DataSensitivity {
  PUBLIC = 'PUBLIC',
  INTERNAL = 'INTERNAL',
  CONFIDENTIAL = 'CONFIDENTIAL',
  PII = 'PII',
  PCI = 'PCI',
  HIPAA = 'HIPAA',
}

export enum SloMetricType {
  LATENCY = 'LATENCY',
  THROUGHPUT = 'THROUGHPUT',
  AVAILABILITY = 'AVAILABILITY',
  ERROR_RATE = 'ERROR_RATE',
  SATURATION = 'SATURATION',
  CUSTOM = 'CUSTOM',
}

export interface GatewayRouteSloTarget {
  metric: SloMetricType;
  sliDescription?: string | null;
  sloTarget: string;
  measurementWindow?: string | null;
  notes?: string | null;
}

export interface GatewayRoute extends BaseEntity {
  basePath: string;
  upstreamServer: string;
  upstreamPath: string;
  envName: string;
  gatewayId: string;
  gatewaySku?: GatewaySku | null;
  gatewayAuthMechanism: RouteAuthMechanism;
  gatewayAuthNotes?: string | null;
  upstreamAuthMechanism: RouteAuthMechanism;
  upstreamAuthNotes?: string | null;
  purpose?: string | null;
  dataSensitivity?: DataSensitivity | null;
  notes?: string | null;
  sloTargets: GatewayRouteSloTarget[];
  apis: API[];
  products: Product[];
}

export interface API extends BaseEntity {
  /** Related specification (Specification) */
  specification?: Specification | null;
  
  /** Specification content (String) */
  specContent?: string | null;
  
  /** API version (String!) */
  version: string;
  
  /** Expiration date ISO string (String) */
  expirationDate?: string | null;

  /** Architect agent standards review JSON */
  architectReview?: string | null;

  /** Linked developer guides */
  developerGuides?: DeveloperGuide[];

  /** Linked library documents */
  documents?: Document[];
}

export interface Category extends BaseEntity {
  // No additional fields beyond BaseEntity
}

export interface DocumentParent {
  entityType: EntityType;
  entityId: string;
  name?: string | null;
}

export interface Document extends BaseEntity {
  content?: string | null;
  sourceFormat?: GuideSourceFormat | null;
  sourceFileName?: string | null;
  expirationDate?: string | null;
  ragIndexStatus?: RagIndexStatus | null;
  parents?: DocumentParent[];
}

export interface LibraryQueryPlan {
  documentTypes?: string[];
  parentTypes?: string[];
  topics?: string[];
  artifactHints?: string[];
  lifecycleHints?: string[];
  subjectType?: string | null;
  semanticQuery?: string | null;
}

export interface LibrarySearchHit {
  documentId: string;
  name: string;
  sectionType?: string | null;
  parents?: DocumentParent[];
  excerpt?: string | null;
  content?: string | null;
  score?: number | null;
  inferredQuery?: LibraryQueryPlan | null;
}

export interface LibrarySearchResponse {
  inferredQuery?: LibraryQueryPlan | null;
  hits: LibrarySearchHit[];
}

export interface OperationalError extends BaseEntity {
  code?: string | null;
  httpStatus?: number | null;
  category?: OperationalErrorCategory | null;
  message?: string | null;
  remediation?: string | null;
  errorStrings: string[];
  products: Product[];
  apis: API[];
}

export enum KafkaSchemaFormat {
  JSON = 'JSON',
  AVRO = 'AVRO',
  TEXT = 'TEXT',
}

export enum SchemaCompatibilityLevel {
  NONE = 'NONE',
  BACKWARD = 'BACKWARD',
  BACKWARD_TRANSITIVE = 'BACKWARD_TRANSITIVE',
  FORWARD = 'FORWARD',
  FORWARD_TRANSITIVE = 'FORWARD_TRANSITIVE',
  FULL = 'FULL',
  FULL_TRANSITIVE = 'FULL_TRANSITIVE',
}

export interface KafkaEventTopic extends BaseEntity {
  environment: string;
  purpose?: string | null;
  schemaFormat: KafkaSchemaFormat;
  schemaContent: string;
  schemaVersion?: string | null;
  schemaSubject?: string | null;
  schemaRegistryUrl?: string | null;
  compatibilityLevel?: SchemaCompatibilityLevel | null;
  keySchemaFormat?: KafkaSchemaFormat | null;
  keySchemaContent?: string | null;
  partitionCount?: number | null;
  replicationFactor?: number | null;
  retentionMs?: string | null;
  samplePayload?: string | null;
  dataSensitivity?: DataSensitivity | null;
  notes?: string | null;
  products: Product[];
  apis: API[];
}

export interface FAQ extends BaseEntity {
  question?: string | null;
  answer?: string | null;
}

export interface DeveloperGuideCodeSample {
  id: string;
  language: string;
  label: string;
  code: string;
}

export interface DeveloperGuideSection {
  id: string;
  sortOrder: number;
  title: string;
  instructions: string;
  codeSamples: DeveloperGuideCodeSample[];
}

export interface DeveloperGuide extends BaseEntity {
  sections: DeveloperGuideSection[];
  apis: API[];
  products: Product[];
}

export interface Link extends BaseEntity {
  // No additional fields beyond BaseEntity
}

export interface Media extends BaseEntity {
  // No additional fields beyond BaseEntity
}

export interface MCPServer extends BaseEntity {
  // No additional fields beyond BaseEntity
}

export interface Specification extends BaseEntity {
  // No additional fields beyond BaseEntity
}

export enum SupportCaseStatus {
  OPEN = 'OPEN',
  ASSIGNED = 'ASSIGNED',
  IN_PROGRESS = 'IN_PROGRESS',
  AWAITING_RESPONSE = 'AWAITING_RESPONSE',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export enum SupportCaseAssigneeType {
  PM = 'PM',
  CSR = 'CSR',
}

export enum SupportCaseEventType {
  STATUS_CHANGE = 'STATUS_CHANGE',
  ASSIGNMENT = 'ASSIGNMENT',
  COMMENT = 'COMMENT',
  AUTO_CLOSE = 'AUTO_CLOSE',
  RESOLUTION_ACCEPTED = 'RESOLUTION_ACCEPTED',
}

export enum SupportCaseCommentType {
  INSTRUCTION = 'INSTRUCTION',
  RESPONSE = 'RESPONSE',
  SYSTEM = 'SYSTEM',
}

export interface SupportCaseProductRef {
  uuid: string;
  name: string;
}

export interface SupportCaseEvent {
  uuid: string;
  tenantId: string;
  supportCaseId: string;
  eventType: SupportCaseEventType;
  actorEmail: string;
  fromStatus?: SupportCaseStatus | null;
  toStatus?: SupportCaseStatus | null;
  message?: string | null;
  metadata?: string | null;
  createdOn: string;
}

export interface SupportCaseComment {
  uuid: string;
  tenantId: string;
  supportCaseId: string;
  authorEmail: string;
  body: string;
  commentType: SupportCaseCommentType;
  createdOn: string;
}

export interface TenantSupportCaseSettings {
  tenantId: string;
  autoCloseResolvedDays: number;
  updatedBy?: string | null;
  updatedOn?: string | null;
}

export enum TenantMemberStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export enum TenantStatus {
  ACTIVE = "ACTIVE",
  DISABLED = "DISABLED",
}

export interface PlatformTenant {
  tenantId: string;
  displayName?: string | null;
  status: TenantStatus;
  createdBy: string;
  createdOn: string;
  updatedOn?: string | null;
}

export interface PlatformTenantRoleCount {
  role: string;
  count: number;
}

export interface PlatformTenantInsight {
  tenantId: string;
  displayName?: string | null;
  status: TenantStatus;
  totalUsers: number;
  usersByRole: PlatformTenantRoleCount[];
  productCount: number;
  apiCount: number;
  categoryCount: number;
  faqCount: number;
  linkCount: number;
  documentCount: number;
  supportCaseCount: number;
}

export interface PlatformTenantInsightsSummary {
  totalTenants: number;
  totalActiveTenants: number;
  totalDisabledTenants: number;
  tenants: PlatformTenantInsight[];
}

export interface AgentRunnerBinding {
  uuid: string;
  tenantId?: string | null;
  agentId?: string | null;
  basePath: string;
  updatedBy: string;
  updatedOn?: string | null;
}

export interface TenantOrganizationSettings {
  tenantId: string;
  companyName?: string | null;
  address?: string | null;
  website?: string | null;
  phone?: string | null;
  contactEmail?: string | null;
  updatedBy?: string | null;
  updatedOn?: string | null;
}

export interface TenantContactInfo {
  companyName?: string | null;
  address?: string | null;
  website?: string | null;
  phone?: string | null;
  contactEmail?: string | null;
}

export interface TenantDataExport {
  tenantId: string;
  exportedAt: string;
  exportedBy: string;
  filename: string;
  json: string;
}

export interface TenantDataPurgeResult {
  tenantId: string;
  removedMembers: number;
  message: string;
}

export interface TenantMember {
  tenantId: string;
  email: string;
  displayName?: string | null;
  status: TenantMemberStatus;
  roles: string[];
  createdBy: string;
  createdOn: string;
  updatedOn?: string | null;
}

export interface ProductManagementGroup {
  uuid: string;
  tenantId: string;
  name: string;
  description?: string | null;
  owners: string[];
  members: string[];
  createdBy: string;
  createdOn: string;
  updatedOn: string;
  assignedAssetCount: number;
}

export interface EntityPmgAssignment {
  entityUuid: string;
  entityType?: EntityType | null;
  entityName?: string | null;
  pmgUuid: string;
  pmgName: string;
}

export interface SupportCase extends BaseEntity {
  caseStatus: SupportCaseStatus;
  assigneeEmail?: string | null;
  assigneeType?: SupportCaseAssigneeType | null;
  product?: SupportCaseProductRef | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  events?: SupportCaseEvent[];
  comments?: SupportCaseComment[];
}

export interface AIAgent extends BaseEntity {
  /** AI Agent card ID (String) */
  agentCardId?: string | null;
  
  /** AI Agent card content (String) */
  agentCardContent?: string | null;
}

export interface AppKyc extends BaseEntity {
  appKeyId: string;
  providerGatewayType: string;
  providerGatewayIdentifier: string;
  appKeyStatus: AppKeyStatus;
  keyCreatedOn?: string | null;
  keyExpiresOn?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactCompanyName?: string | null;
  contactCompanyWebsite?: string | null;
  bmContactName?: string | null;
  bmContactEmail?: string | null;
  itSupportName?: string | null;
  itSupportEmail?: string | null;
}

/** Product attached to a subscription with per-product approval status */
export interface SubscriptionProductEntry {
  product: Product;
  productStatus?: boolean;
}

/** Subscription container; may include multiple products */
export interface ProductSubscription extends BaseEntity {
  subscriberUserId: string;
  subscriberEmail: string;
  products: SubscriptionProductEntry[];
}

// UI Specific Types (for display purposes)
export interface UIProduct {
  id: string;
  name: string;
  description: string;
  /** Primary category label for display */
  category: string;
  /** All linked category names (for filtering) */
  categoryNames: string[];
  tags: string[];
  heroImage?: string | null; // Changed from Media object to string URL
  status: EntityStatus;
  type: EntityType;
  subscribable?: boolean;
  rating?: number; // For UI display if needed
}

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon?: React.ReactNode;
  children?: NavItem[];
}

// =============================================
// INPUT TYPES (For Mutations)
// =============================================

export interface CreateEntityInput {
  type: EntityType;
  name: string;
  description?: string | null;
  tags?: string[];
  classifications?: string | null;
  entityClass?: string | null;
  status?: EntityStatus;
}

export interface CreateProductInput extends CreateEntityInput {
  heroImageId?: string | null;
  relatedProductIds?: string[];
  categoryIds?: string[];
  apiIds?: string[];
  documentIds?: string[];
  faqIds?: string[];
  linkIds?: string[];
  approvalNeeded?: boolean;
  productOwners?: string[];
}

export interface CreateAPIInput extends CreateEntityInput {
  specificationId?: string | null;
  specContent?: string | null;
  version: string;
  expirationDate?: string | null;
}

export interface CreateAIAgentInput extends CreateEntityInput {
  agentCardId?: string | null;
  agentCardContent?: string | null;
}

export interface UpdateProductInput extends Partial<CreateProductInput> {
  uuid: string;
}

export interface UpdateAPIInput extends Partial<CreateAPIInput> {
  uuid: string;
}

export interface UpdateAIAgentInput extends Partial<CreateAIAgentInput> {
  uuid: string;
}

export interface UpdateEntityInput extends Partial<CreateEntityInput> {
  uuid: string;
}

// =============================================
// QUERY/MUTATION TYPES
// =============================================

export interface GraphQLError {
  message: string;
  locations?: Array<{ line: number; column: number }>;
  path?: string[];
  extensions?: Record<string, any>;
}

export interface QueryResponse<T = any> {
  data?: T;
  errors?: GraphQLError[];
}

export interface MutationResponse<T = any> {
  data?: {
    [key: string]: T;
  };
  errors?: GraphQLError[];
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// =============================================
// QUERY RESPONSE TYPES
// =============================================

export type ProductsQueryResponse = QueryResponse<{ products: Product[] }>;
export type ProductQueryResponse = QueryResponse<{ product: Product }>;

export type CategoriesQueryResponse = QueryResponse<{ categories: Category[] }>;
export type CategoryQueryResponse = QueryResponse<{ category: Category }>;

export type APIsQueryResponse = QueryResponse<{ apis: API[] }>;
export type APIQueryResponse = QueryResponse<{ api: API }>;

export type DocumentsQueryResponse = QueryResponse<{ documents: Document[] }>;
export type DocumentQueryResponse = QueryResponse<{ document: Document }>;

export type FAQsQueryResponse = QueryResponse<{ faqs: FAQ[] }>;
export type FAQQueryResponse = QueryResponse<{ faq: FAQ }>;

export type LinksQueryResponse = QueryResponse<{ links: Link[] }>;
export type LinkQueryResponse = QueryResponse<{ link: Link }>;

export type MediaListQueryResponse = QueryResponse<{ media: Media[] }>;
export type MediaItemQueryResponse = QueryResponse<{ mediaItem: Media }>;

export type MCPServersQueryResponse = QueryResponse<{ mcpServers: MCPServer[] }>;
export type MCPServerQueryResponse = QueryResponse<{ mcpServer: MCPServer }>;

export type SpecificationsQueryResponse = QueryResponse<{ specifications: Specification[] }>;
export type SpecificationQueryResponse = QueryResponse<{ specification: Specification }>;

export type SupportCasesQueryResponse = QueryResponse<{ supportCases: SupportCase[] }>;
export type SupportCaseQueryResponse = QueryResponse<{ supportCase: SupportCase }>;

export type AIAgentsQueryResponse = QueryResponse<{ aiAgents: AIAgent[] }>;
export type AIAgentQueryResponse = QueryResponse<{ aiAgent: AIAgent }>;

// =============================================
// MUTATION RESPONSE TYPES
// =============================================

export type CreateProductResponse = MutationResponse<Product>;
export type CreateAPIResponse = MutationResponse<API>;
export type CreateCategoryResponse = MutationResponse<Category>;
export type CreateDocumentResponse = MutationResponse<Document>;
export type CreateFAQResponse = MutationResponse<FAQ>;
export type CreateLinkResponse = MutationResponse<Link>;
export type CreateMediaResponse = MutationResponse<Media>;
export type CreateMCPServerResponse = MutationResponse<MCPServer>;
export type CreateSpecificationResponse = MutationResponse<Specification>;
export type CreateSupportCaseResponse = MutationResponse<SupportCase>;
export type CreateAIAgentResponse = MutationResponse<AIAgent>;

export type UpdateProductResponse = MutationResponse<Product>;
export type UpdateAPIResponse = MutationResponse<API>;
export type UpdateCategoryResponse = MutationResponse<Category>;
export type UpdateDocumentResponse = MutationResponse<Document>;
export type UpdateFAQResponse = MutationResponse<FAQ>;
export type UpdateLinkResponse = MutationResponse<Link>;
export type UpdateMediaResponse = MutationResponse<Media>;
export type UpdateMCPServerResponse = MutationResponse<MCPServer>;
export type UpdateSpecificationResponse = MutationResponse<Specification>;
export type UpdateSupportCaseResponse = MutationResponse<SupportCase>;
export type UpdateAIAgentResponse = MutationResponse<AIAgent>;

export type DeleteResponse = MutationResponse<{ success: boolean; message?: string }>;

// =============================================
// FILTER & PAGINATION TYPES
// =============================================

export interface EntityFilter {
  status?: EntityStatus;
  type?: EntityType;
  tags?: string[];
  search?: string;
  createdBy?: string;
  dateRange?: {
    start: string;
    end: string;
  };
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// =============================================
// HELPER TYPES & UTILITIES
// =============================================

export type Entity = 
  | Product 
  | API 
  | Category 
  | Document 
  | FAQ 
  | Link 
  | Media 
  | MCPServer 
  | Specification 
  | SupportCase 
  | AIAgent;

export type EntityTypeMap = {
  [EntityType.PRODUCT]: Product;
  [EntityType.API]: API;
  [EntityType.CATEGORY]: Category;
  [EntityType.DOCUMENT]: Document;
  [EntityType.FAQ]: FAQ;
  [EntityType.LINK]: Link;
  [EntityType.MEDIA]: Media;
  [EntityType.MCP_SERVER]: MCPServer;
  [EntityType.SPECIFICATION]: Specification;
  [EntityType.SUPPORT_CASE]: SupportCase;
  [EntityType.AI_AGENT]: AIAgent;
};

export type CreateInputMap = {
  [EntityType.PRODUCT]: CreateProductInput;
  [EntityType.API]: CreateAPIInput;
  [EntityType.CATEGORY]: CreateEntityInput;
  [EntityType.DOCUMENT]: CreateEntityInput;
  [EntityType.FAQ]: CreateEntityInput;
  [EntityType.LINK]: CreateEntityInput;
  [EntityType.MEDIA]: CreateEntityInput;
  [EntityType.MCP_SERVER]: CreateEntityInput;
  [EntityType.SPECIFICATION]: CreateEntityInput;
  [EntityType.SUPPORT_CASE]: CreateEntityInput;
  [EntityType.AI_AGENT]: CreateAIAgentInput;
};

export type UpdateInputMap = {
  [EntityType.PRODUCT]: UpdateProductInput;
  [EntityType.API]: UpdateAPIInput;
  [EntityType.CATEGORY]: UpdateEntityInput;
  [EntityType.DOCUMENT]: UpdateEntityInput;
  [EntityType.FAQ]: UpdateEntityInput;
  [EntityType.LINK]: UpdateEntityInput;
  [EntityType.MEDIA]: UpdateEntityInput;
  [EntityType.MCP_SERVER]: UpdateEntityInput;
  [EntityType.SPECIFICATION]: UpdateEntityInput;
  [EntityType.SUPPORT_CASE]: UpdateEntityInput;
  [EntityType.AI_AGENT]: UpdateAIAgentInput;
};

// =============================================
// COMPONENT PROPS TYPES
// =============================================

export interface EntityCardProps<T extends Entity = Entity> {
  entity: T;
  onEdit?: (entity: T) => void;
  onDelete?: (id: string) => void;
  onClick?: (entity: T) => void;
  className?: string;
}

export interface EntityListProps<T extends Entity = Entity> {
  entities: T[];
  loading?: boolean;
  error?: string;
  onEntityClick?: (entity: T) => void;
  onEdit?: (entity: T) => void;
  onDelete?: (id: string) => void;
  emptyMessage?: string;
}

export interface EntityFormProps<T extends Entity = Entity> {
  entity?: T;
  onSubmit: (data: any) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  error?: string;
}

// =============================================
// API CLIENT TYPES
// =============================================

export interface GraphQLClientConfig {
  endpoint: string;
  headers?: Record<string, string>;
}

export interface GraphQLRequest {
  query: string;
  variables?: Record<string, any>;
  operationName?: string;
}

// =============================================
// STATE MANAGEMENT TYPES
// =============================================

export interface EntityState<T extends Entity = Entity> {
  items: Record<string, T>;
  loading: boolean;
  error: string | null;
  selectedId: string | null;
  filters: EntityFilter;
  pagination: PaginationParams & {
    total: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface AppState {
  products: EntityState<Product>;
  apis: EntityState<API>;
  categories: EntityState<Category>;
  documents: EntityState<Document>;
  faqs: EntityState<FAQ>;
  links: EntityState<Link>;
  media: EntityState<Media>;
  mcpServers: EntityState<MCPServer>;
  specifications: EntityState<Specification>;
  supportCases: EntityState<SupportCase>;
  aiAgents: EntityState<AIAgent>;
}

// =============================================
// AUTH (JWT / me)
// =============================================

export interface JwtResponse {
  token: string;
  token_type?: string;
}

export interface UserTenant {
  tenantId: string;
  displayName?: string | null;
  roles: string[];
}

export interface TenantJwtResponse {
  token: string;
  tokenType?: string;
  tenantId: string;
  expiresIn?: number;
}

export interface AuthUser {
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
  groups: string[];
  tenantId: string;
}

export interface RegisterOrganizationPayload {
  preferredOrgName: string;
  companyName: string;
  address?: string;
  website?: string;
  phone?: string;
  contactEmail?: string;
}

export interface RegisterOrganizationResponse {
  tenantId: string;
  preferredOrgName: string;
  companyName: string;
  address?: string | null;
  website?: string | null;
  phone?: string | null;
  contactEmail?: string | null;
  adminEmail: string;
}

export interface PersonalAccessToken {
  id: string;
  name: string;
  tokenPrefix: string;
  expiresAt: string;
  revokedAt?: string | null;
  lastUsedAt?: string | null;
  createdOn: string;
  revoked: boolean;
  expired: boolean;
}

export interface CreatePersonalAccessTokenPayload {
  name: string;
  expiresInDays?: number;
}

export interface CreatePersonalAccessTokenResult {
  id: string;
  name: string;
  token: string;
  tokenPrefix: string;
  expiresAt: string;
  createdOn: string;
}